import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiOutlineTrash } from 'react-icons/hi'

import { Box, Center, Flex, FormLabel, Text } from '@chakra-ui/react'

import {
  Button as DarkButton,
  DarkProvider,
  FieldLabel,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Button } from '@/components/ui'

const DEFAULT_FLOOR_PLAN_ROW_HEIGHT = '48px'
const LEGACY_DANGER_COLOR = '#D01030'
const LEGACY_DANGER_HOVER_COLOR = '#b40e2a'
const LEGACY_DANGER_SHADOW = '0 2px 7px 0 rgba(189,25,25,0.5)'
const LEGACY_FLOOR_PLAN_BORDER_COLOR = 'rgb(159, 184, 229)'
const LEGACY_FLOOR_PLAN_TEXT_COLOR = 'rgb(153, 153, 153)'
const LEGACY_ON_DANGER_COLOR = '#fff'
const LEGACY_PENDING_REMOVAL_HEIGHT = '40px'
const REBRAND_PENDING_REMOVAL_HEIGHT = '36px'

interface FloorPlanRemoveIProps {
  colorScheme?: ColorScheme
  floorPlanName?: string
  isMarkedForRemoval?: boolean
  isUpdateLoading?: boolean
  onRemove?: () => void
  onUndo?: () => void
}

export const FloorPlanRemove = ({
  colorScheme,
  floorPlanName,
  isMarkedForRemoval = false,
  onRemove,
  onUndo,
  isUpdateLoading,
}: FloorPlanRemoveIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const rebrandTokenPrefix = colorScheme === 'light' ? 'light.' : ''
  const rebrandColor = (token: string) =>
    tokenVar(`colors.${rebrandTokenPrefix}${token}`)
  const removalBorderColor = colorScheme
    ? rebrandColor('interactive.danger')
    : LEGACY_DANGER_COLOR
  const defaultBorderColor = colorScheme
    ? rebrandColor('border.search')
    : LEGACY_FLOOR_PLAN_BORDER_COLOR
  const floorPlanTextColor = colorScheme
    ? rebrandColor('text.secondary')
    : LEGACY_FLOOR_PLAN_TEXT_COLOR
  const pendingMessageColor = colorScheme
    ? rebrandColor('text.secondary')
    : `text.secondary.${mode}`
  const removeButtonHoverColor = colorScheme
    ? rebrandColor('interactive.danger-hover')
    : LEGACY_DANGER_HOVER_COLOR
  const removeButtonIconColor = colorScheme
    ? rebrandColor('text.on-danger')
    : LEGACY_ON_DANGER_COLOR
  const isUsingRebrandRemovalStyle = Boolean(colorScheme && isMarkedForRemoval)
  // Dark TextInput and Chakra's legacy md controls do not share a height
  // token, so keep their public defaults named here alongside the original row.
  const pendingRemovalHeight = colorScheme
    ? REBRAND_PENDING_REMOVAL_HEIGHT
    : LEGACY_PENDING_REMOVAL_HEIGHT
  const floorPlanRowHeight = isMarkedForRemoval
    ? pendingRemovalHeight
    : DEFAULT_FLOOR_PLAN_ROW_HEIGHT

  return (
    <>
      {colorScheme ? (
        <DarkProvider colorScheme={colorScheme}>
          <FieldLabel colorScheme={colorScheme}>
            {t('floors:crud_remove_floor_plan_label')}
          </FieldLabel>
        </DarkProvider>
      ) : (
        <FormLabel color={`text.primary.${mode}`}>
          {t('floors:crud_remove_floor_plan_label')}
        </FormLabel>
      )}
      <Flex
        alignItems='center'
        height={floorPlanRowHeight}
        justifyContent='space-between'
      >
        <Box
          borderColor={
            isMarkedForRemoval ? removalBorderColor : defaultBorderColor
          }
          borderRadius={isUsingRebrandRemovalStyle ? '0px' : '6px'}
          borderStyle='solid'
          borderWidth={isUsingRebrandRemovalStyle ? '1px' : '2px'}
          data-testid='EditFloorModal:floorPlanFile'
          flex='1'
          height={floorPlanRowHeight}
          mr={isMarkedForRemoval ? 0 : 2}
        >
          <Flex alignItems='center' height='100%'>
            <Text
              color={floorPlanTextColor}
              fontSize='14px'
              fontWeight='600'
              ml={4}
              noOfLines={1}
              textDecoration={isMarkedForRemoval ? 'line-through' : 'none'}
            >
              {floorPlanName}
              .png
            </Text>
          </Flex>
        </Box>
        {!isMarkedForRemoval && (
          <Center
            _hover={isUpdateLoading ? {} : { bgColor: removeButtonHoverColor }}
            aria-disabled={isUpdateLoading}
            aria-label={t('floors:crud_remove_button_aria')}
            bgColor={removalBorderColor}
            boxShadow={colorScheme ? 'none' : LEGACY_DANGER_SHADOW}
            cursor={isUpdateLoading ? 'not-allowed' : 'pointer'}
            data-testid='EditFloorModal:removeFloorButton'
            h='full'
            minW='48px'
            onClick={isUpdateLoading ? undefined : onRemove}
            opacity={isUpdateLoading ? 0.5 : 1}
            role='button'
            rounded={colorScheme ? '0px' : '5px'}
            transition='ease-in-out'
            transitionDuration='300ms'
            transitionProperty='background-color'
            w='48px'
          >
            <HiOutlineTrash color={removeButtonIconColor} size='21px' />
          </Center>
        )}
      </Flex>
      {isMarkedForRemoval && (
        <Flex alignItems='center' justifyContent='space-between' mt={2}>
          <Text
            color={pendingMessageColor}
            data-testid='EditFloorModal:pendingFloorPlanRemoval'
            fontSize='14px'
            mr={3}
          >
            {t('floors:crud_remove_pending')}
          </Text>
          {colorScheme ? (
            <DarkButton
              colorScheme={colorScheme}
              data-testid='EditFloorModal:undoFloorPlanRemoval'
              disabled={isUpdateLoading}
              onClick={onUndo}
              size='sm'
              variant='tertiary'
            >
              {t('floors:crud_remove_undo')}
            </DarkButton>
          ) : (
            <Button
              data-testid='EditFloorModal:undoFloorPlanRemoval'
              isDisabled={isUpdateLoading}
              onClick={onUndo}
              size='sm'
              variant='link'
            >
              {t('floors:crud_remove_undo')}
            </Button>
          )}
        </Flex>
      )}
    </>
  )
}
